import {
  SettingsIcon,
  ClockIcon,
  LinkIcon,
  CheckIcon,
  ShieldCheckIcon,
  BellIcon,
} from "lucide-react";
import { ComponentProps } from "react";
import {
  FieldValues,
  useForm,
  useFormContext,
  UseFormProps,
  useFormState,
} from "react-hook-form";

import {
  ConnectionBridgeBaseFragment,
  IntegrationConfigurationFragment,
  useGenerateConnectionLabelLazyQuery,
} from "@/apollo/types";
import AppearTransition from "@/components/elements/AppearTransition";
import { ActionButton, Button } from "@/components/elements/Button";
import Tooltip from "@/components/elements/Tooltip";
import { WeldTab } from "@/components/elements/tabs";
import FormTextInput from "@/components/modules/FormFactory/FormTextInput";
import {
  Input,
  InputAddonLeft,
  InputGroup,
} from "@/components/primitives/input";
import { useCopyToClipboard } from "@/hooks/useCopyToClipboard";
import { isCustomConnector } from "@/integrations";
import { useCreateConnectionBridge } from "@/integrations/hooks/useCreateConnectionBridge";
import { useToast } from "@/providers/ToastProvider";
import { Tab } from "@headlessui/react";

import { snakeCase } from "lodash";
import FormDisplay from "./FormDisplay";
import { GoogleSignInButton, usesGoogleSignIn } from "./GoogleSignInButton";
import { AuthorizationLinkFeatureGuard } from "@/pages/Connections/components/AuthorizationLinkFeatureGuard";
import { AuthorizationLinkTeaser } from "@/pages/Connections/components/AuthorizationLinkTeaser";
import { FormatTime } from "@/components/elements/FormatTime";
import cn from "@/helpers/classNames";

export enum AuthorizeMethod {
  Self = "self",
  External = "external",
}
export type FormModel = FieldValues & {
  label: string;
};

type UseConnectionForm = ReturnType<typeof useForm<FormModel>>;

export function useConnectionForm(
  integration: IntegrationConfigurationFragment,
  options?: UseFormProps<FormModel>,
): UseConnectionForm;

// Overload 2: with `label` as second argument
export function useConnectionForm(
  integration: IntegrationConfigurationFragment,
  label: string,
  options?: UseFormProps<FormModel>,
): UseConnectionForm;

// Actual implementation
export function useConnectionForm(
  integration: IntegrationConfigurationFragment,
  labelOrOptions?: string | UseFormProps<FormModel>,
  maybeOptions?: UseFormProps<FormModel>,
) {
  let label: string | undefined;
  let options: UseFormProps<FormModel>;

  if (typeof labelOrOptions === "string") {
    label = labelOrOptions;
    options = maybeOptions || {};
  } else {
    options = labelOrOptions || {};
  }

  const [generateConnectionLabel] = useGenerateConnectionLabelLazyQuery();

  return useForm<FormModel>({
    mode: "onChange",
    ...options,
    defaultValues: async () => {
      let defaultLabel = label;
      if (!defaultLabel) {
        const { data: labelData } = await generateConnectionLabel({
          variables: {
            baseLabel: isCustomConnector(integration.id)
              ? integration.name
              : integration.id,
          },
          fetchPolicy: "no-cache",
        });
        defaultLabel =
          labelData?.generateConnectionLabel || snakeCase(integration.name);
      }

      if (!integration.requiresConfiguration || !integration.forms.length) {
        return {
          label: defaultLabel,
          selectedForm: {},
        };
      }

      const selectedForm =
        integration.forms.find((f: any) => f.default) ?? integration.forms[0];

      return {
        ...selectedForm.defaultValues,
        ...(typeof options.defaultValues === "function"
          ? await options.defaultValues()
          : options.defaultValues),
        label: defaultLabel,
        selectedForm,
      };
    },
  });
}

type Props = {
  integration: IntegrationConfigurationFragment;
  connectionId?: string;
  authorizeMethods?: AuthorizeMethod[];
  onExternalAuthLinkCreated?: (
    connectionBridge: ConnectionBridgeBaseFragment,
  ) => void;
  isLoading: boolean;
  onAuthorizeMethodChanged?: (method: AuthorizeMethod) => void;
  defaultAuthorizeMethod?: AuthorizeMethod;
  currentConnectionFormId?: string | null;
};

export default function ConnectionForm({
  integration,
  connectionId,
  authorizeMethods = [AuthorizeMethod.Self],
  onExternalAuthLinkCreated,
  onAuthorizeMethodChanged,
  defaultAuthorizeMethod = AuthorizeMethod.Self,
  currentConnectionFormId,
}: Props) {
  const form = useFormContext<FormModel>();
  const isLoadingDefaultValues = form.formState.isLoading;

  if (!integration.requiresConfiguration) {
    authorizeMethods = authorizeMethods.filter(
      (method: AuthorizeMethod) => method !== AuthorizeMethod.External,
    );
  }

  const setSelectedForm = (formId: string) => {
    const selectedForm = integration.forms.find((f: any) => f.id === formId);
    if (!selectedForm) {
      return;
    }

    form.setValue("selectedForm", selectedForm);
    if (selectedForm.defaultValues) {
      Object.entries(selectedForm.defaultValues).forEach(([key, value]) => {
        form.setValue(key, value);
      });
    }
  };

  const toast = useToast();
  const {
    createConnectionBridge,
    loading: isCreatingConnectionBridge,
    data,
  } = useCreateConnectionBridge({
    onError(error) {
      toast("Creating link failed", error.message, "error");
    },
    onSuccess(connectionBridge) {
      onExternalAuthLinkCreated?.(connectionBridge);
    },
  });

  const handleGenerateLink = async () => {
    const label: string = form.getValues("label");
    await createConnectionBridge(integration, label, connectionId);
  };

  const isConnectionBridgeCreated = !!data?.createConnectionBridge;
  const isEditMode = !!connectionId;

  if (isLoadingDefaultValues) {
    return (
      <div className="flex h-32 items-center justify-center">
        <p className="text-sm text-muted-foreground">Loading...</p>
      </div>
    );
  }

  return (
    <>
      {!isEditMode && (
        <FormTextInput
          field={{
            label: "Name",
            required: true,
            name: "label",
            type: "text",
            description:
              "Enter a name for this connection. This name will be used to identify the connection for both data source (ELT) syncs and data activation (reverse ETL) syncs",
            inputProps: {
              disabled: isConnectionBridgeCreated,
            },
          }}
        />
      )}
      <Tab.Group
        as="div"
        onChange={(index) => {
          let method = authorizeMethods[index];
          onAuthorizeMethodChanged?.(method);
        }}
        defaultIndex={Math.max(
          authorizeMethods.indexOf(defaultAuthorizeMethod),
          0,
        )}
        className={cn({
          "mt-6": !isEditMode,
        })}
      >
        <Tab.List
          className={cn(
            "mb-6 flex gap-8",
            authorizeMethods.length < 2 ? "hidden" : "",
          )}
        >
          {authorizeMethods.map((method: AuthorizeMethod) => {
            switch (method) {
              case AuthorizeMethod.Self:
                return (
                  <WeldTab
                    className="h-6"
                    disabled={isCreatingConnectionBridge || data != null}
                    key={method}
                    groupId="connection-form-authorize-method"
                  >
                    <SettingsIcon className="mr-1 size-4" />
                    Configure
                  </WeldTab>
                );
              case AuthorizeMethod.External:
                return (
                  <Tooltip
                    content="Need someone else to authorize this connection?"
                    key={method}
                  >
                    <WeldTab
                      className="h-6"
                      groupId="connection-form-authorize-method"
                    >
                      <LinkIcon className="mr-1 size-4" />
                      Share Authorization Link
                    </WeldTab>
                  </Tooltip>
                );
              default:
                return <></>;
            }
          })}
        </Tab.List>
        <Tab.Panels>
          {authorizeMethods.map((method: AuthorizeMethod) => {
            switch (method) {
              case AuthorizeMethod.Self:
                return (
                  <Tab.Panel key={method}>
                    <FormDisplay
                      onChangeForm={setSelectedForm}
                      integration={integration}
                      isEditMode={isEditMode}
                      currentFormId={currentConnectionFormId}
                    />
                  </Tab.Panel>
                );
              case AuthorizeMethod.External:
                return (
                  <Tab.Panel key={method}>
                    <AuthorizationLinkFeatureGuard
                      fallback={() => (
                        <div className="mx-auto mt-10 w-full max-w-lg rounded-sm border bg-popover p-8">
                          <AuthorizationLinkTeaser />
                        </div>
                      )}
                    >
                      <GetAuthorizationLinkPanel
                        integration={integration}
                        onGenerateLink={handleGenerateLink}
                        isGeneratingLink={isCreatingConnectionBridge}
                        connectionBridge={data?.createConnectionBridge}
                        hideCloseText={isEditMode}
                      />
                    </AuthorizationLinkFeatureGuard>
                  </Tab.Panel>
                );
              default:
                return <></>;
            }
          })}
        </Tab.Panels>
      </Tab.Group>
    </>
  );
}

function GetAuthorizationLinkPanel(props: {
  integration: IntegrationConfigurationFragment;
  onGenerateLink: () => void;
  isGeneratingLink?: boolean;
  connectionBridge?: ConnectionBridgeBaseFragment;
  isDisabled?: boolean;
  hideCloseText?: boolean;
}) {
  const toast = useToast();
  const [, copy] = useCopyToClipboard();
  return (
    <div className="space-y-4 text-sm">
      <div className="space-y-2">
        <p>Need someone else to authorize this connection?</p>
        <p>
          Generate a secure link that someone else can use to authorize this
          connection. This is useful when you don’t have direct access to the
          required account or data source.
        </p>
        <p>
          Click <strong>Create Authorization Link</strong> to create and copy
          the authorization URL.
        </p>
      </div>
      <div className="space-y-8">
        <div className="mt-8 flex flex-col items-center">
          <Button
            variant="solid"
            colorScheme={props.connectionBridge ? "success" : "primary"}
            icon={props.connectionBridge ? <CheckIcon /> : <LinkIcon />}
            isLoading={props.isGeneratingLink}
            isDisabled={props.isDisabled}
            loadingText="Generating Link..."
            onClick={() => {
              if (props.connectionBridge) {
                return;
              }
              props.onGenerateLink();
            }}
          >
            {props.connectionBridge
              ? "Link Created"
              : "Create Authorization Link"}
          </Button>
        </div>
        {props.connectionBridge && (
          <AppearTransition>
            <div className="rounded-xs border bg-muted p-6">
              <h3 className="mb-4 flex items-center text-base font-medium">
                {" "}
                Link Ready to Share
              </h3>
              <div className="space-y-3">
                <p>
                  Share this link with a person who can grant access on your
                  behalf.
                </p>
                <div className="flex items-center gap-2">
                  <InputGroup className="flex-1">
                    <InputAddonLeft>
                      <LinkIcon className="size-5" />
                    </InputAddonLeft>
                    <Input
                      readOnly
                      defaultValue={props.connectionBridge.authorizeUrl}
                      size="md"
                      className="text-sm"
                    />
                  </InputGroup>
                  <Button
                    variant="solid"
                    colorScheme="primary"
                    className="shrink-0"
                    onClick={async () => {
                      if (!props.connectionBridge) {
                        return;
                      }
                      await copy(props.connectionBridge.authorizeUrl);
                      toast(
                        "Copied to clipboard",
                        "The authorization link has been copied to your clipboard.",
                        "success",
                      );
                    }}
                    autoFocus
                  >
                    Copy
                  </Button>
                </div>
                <div className="flex items-center gap-1 text-muted-foreground">
                  <ClockIcon className="size-4" /> This link is valid until
                  <p>
                    <FormatTime
                      date={props.connectionBridge.expiresAt}
                      format="LLL"
                    />
                    .
                  </p>
                </div>
                <div className="space-y-2">
                  {!props.hideCloseText && (
                    <p className="font-semibold">
                      You can now close this dialog.
                    </p>
                  )}
                  <p>
                    Once the authorization is complete, the connection will be
                    ready to use in your syncs.
                  </p>
                  <div className="flex items-center gap-1">
                    <BellIcon className="size-4 text-muted-foreground" /> You’ll
                    be notified as soon as the authorization is completed.
                  </div>
                </div>
              </div>
            </div>
          </AppearTransition>
        )}
      </div>
    </div>
  );
}

export function ConnectionFormSubmitButton(
  props: ComponentProps<typeof Button> & {
    integration: IntegrationConfigurationFragment;
    isLoading: boolean;
    isEditMode?: boolean;
    isDisabled?: boolean;
  },
) {
  const {
    isEditMode = false,
    isLoading,
    integration,
    isDisabled: _isDisabledProp,
    ...buttonProps
  } = props;
  const { isValid } = useFormState();

  const form = useFormContext();

  const isDisabled = !isValid || isLoading || props.isDisabled || false;

  const formValues = form.getValues();
  if (!formValues.selectedForm) {
    return null;
  }

  if (formValues.selectedForm.type === "oauth") {
    return (
      <OAuthConnectButton
        {...buttonProps}
        form="integration-form"
        type="submit"
        isDisabled={isDisabled}
        isLoading={isLoading}
        integrationId={integration.id}
      />
    );
  }

  return (
    <ActionButton
      {...buttonProps}
      form="integration-form"
      type="submit"
      isDisabled={isDisabled}
      icon={<ShieldCheckIcon />}
      loadingText="Connecting..."
      isLoading={isLoading}
    >
      {isEditMode ? "Reconnect" : "Connect"}
    </ActionButton>
  );
}

function OAuthConnectButton({
  integrationId,
  isLoading,
  ...props
}: ComponentProps<typeof Button> & {
  integrationId: string;
}) {
  return (
    <Tooltip
      content={
        <div className="space-y-2">
          <p className="font-semibold">
            Authorize the connection to access your data
          </p>
          <p>
            A pop-up will open for authorization on the provider’s website. If
            the pop-up is blocked by your browser, you will be redirected to the
            authorization page instead.
          </p>
        </div>
      }
    >
      {usesGoogleSignIn(integrationId) ? (
        <GoogleSignInButton
          form={props.form}
          type={props.type}
          isDisabled={props.isDisabled}
          isLoading={isLoading}
          className={props.className}
        />
      ) : (
        <ActionButton {...props} icon={<ShieldCheckIcon />}>
          Authorize
        </ActionButton>
      )}
    </Tooltip>
  );
}
